/*
* Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
*/
import React, {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import classnames from 'classnames';
import {GenericObject} from '@ion/core/src/types/entities';
import {BackButton} from '@ion/dls/src/components/buttons/BackButton';
import {ForwardButton} from '@ion/dls/src/components/buttons/ForwardButton';
import {dlsTabIndicatorBorder} from '@ion/dls/src/theme/helpers';
import Render from '../../../render/Render';
import {dlsShadow} from '../../../theme/theme';
import useThemeContext from '../../../theme/useThemeContext';
import {DLSTheme} from '../../../types/types';
import Tab from '../current/Tab';
import {TabIndicatorProps} from './TabIndicator';

export interface TabPropTypes {
  passSelected?: boolean;
  reInitialize?: boolean;
  // badgeCount/hasAlert are additive - mirrors current/Tab.tsx's own
  // TabProps['tab'] shape (kept as a separate inline type here rather than
  // imported, matching this file's existing convention of not sharing that
  // type with Tab.tsx). No behavior changes here: each tab object is already
  // forwarded to Tab as-is (`tab={tab}` below), so this is a type-only
  // update letting callers pass the new fields through tabItems ...
  tabItems?: {
    tabHeader: string,
    path?: string,
    tag?: string,
    badgeCount?: number,
    hasAlert?: boolean,
    // current theme only - see current/TabIndicator.tsx ...
    indicator?: TabIndicatorProps
  }[];
  onChange?: (event: ChangeEvent<{}>, value: any) => void;
  variant?: 'scrollable' | 'fullWidth' | 'standard';
  value?: number;
  view?: __esri.MapView;
  // overrides forwarded to each Tab's underlying MaterialTab classes -
  // materialRoot/materialSelected/materialWrapper keys ...
  tabOverrides?: (theme: DLSTheme) => GenericObject;
  // stretches the tab row to fill its container's full width, flush against
  // its left edge, instead of hugging the tab items' own combined width
  // with a 24px left inset (this file's own default "Tab Group" layout) -
  // opt-in and off by default, so every existing caller is unaffected; the
  // Tags accordion's fixed-width, edge-to-edge tab bar is what needs it ...
  fullBleed?: boolean;
  // forwarded to each Tab's own `hideBackground` - opt-in and off by
  // default, so every existing caller is unaffected ...
  hideTabBackground?: boolean;
};

interface TabViewStyleProps {
  fullBleed?: boolean;
}

interface ScrollableTabsProps {
  passSelected?: boolean;
  tabItems?: TabPropTypes['tabItems'];
  onChange?: TabPropTypes['onChange'];
  variant: TabPropTypes['variant'];
  value?: number;
  tabOverrides?: TabPropTypes['tabOverrides'];
  hideTabBackground?: TabPropTypes['hideTabBackground'];
  tabClasses: {flexContainer: string, indicator: string, scroller: string, root: string};
}

// isolated behind React.memo so MUI's <Tabs> only actually re-renders (and
// re-runs its own internal indicator/scroll-into-view measurement effects)
// on mount or when the selection/tab list itself changes - not on every
// parent re-render. Without this, the canScrollLeft/canScrollRight state
// updates below (driven by the prev/next scroll buttons) were cascading
// into <Tabs>, re-triggering MUI's own "scroll selected tab into view"
// behavior and fighting the user's manual scroll ...
const ScrollableTabs = memo(({
  passSelected,
  tabItems,
  onChange,
  variant,
  value,
  tabOverrides,
  hideTabBackground,
  tabClasses
}: ScrollableTabsProps) => (
  <Tabs value={value}
    variant={variant}
    onChange={onChange}
    scrollButtons='off'
    indicatorColor={'secondary'}
    textColor={'secondary'}
    classes={tabClasses}>
    {tabItems?.map((tab, index) => {
      const props = {
        selected : passSelected ? value === index : undefined
      };
      return (
        <Tab key={`${index}-${tab.tabHeader}`}
          index={index}
          onClick={onChange}
          tab={tab}
          materialOverrides={tabOverrides}
          hideBackground={hideTabBackground}
          {...props} />
      );
    })}
  </Tabs>
));

const getTabViewStyles = (theme: DLSTheme) => ({
  // superseded by current/Tab.tsx's own per-tab border-bottom (drawn on
  // each Tab's own 44px-tall root, transparent unless selected) - kept in
  // the DOM (MUI still renders it) but hidden, rather than removed, since
  // no JSX here creates it directly ...
  customTabIndicator : {
    bottom                         : '14px',
    backgroundColor                : 'transparent',
    [theme.breakpoints.down('sm')] : {
      bottom : 0
    }
  },
  tabRoot : {
    // hugs just the tab items' own combined width (not the full row) per
    // Figma's "Tab Group" spec - `fullBleed` (e.g. the Tags accordion's
    // fixed-width, edge-to-edge 2-tab bar) stretches to fill its
    // container instead, matching that different Figma "Tab Group" spec ...
    width                          : ({fullBleed}: TabViewStyleProps) => (fullBleed ? '100%' : 'fit-content'),
    height                         : '44px',
    // no border-bottom (or min-height/overflow overrides) here anymore -
    // this element composes with MUI's own base Tabs.root class, whose
    // competing `minHeight`/`overflow` defaults kept fighting whatever was
    // set here in ways that either misaligned or fully hid a border drawn
    // on this element. current/Tab.tsx's materialRoot now draws the Figma
    // "Tab Group" baseline itself on every tab (pastelTeal unselected,
    // darkTeal selected) - since every tab is a sibling in this same row,
    // their lines are naturally flush with each other without this
    // element's own box needing to match anything ...
    [theme.breakpoints.down('sm')] : {
      alignItems : 'center'
    }
  },
  // the outer wrapper - grows to share its row with siblings (e.g. the
  // grid toolbar) but can also shrink below its content's natural width,
  // which is what lets scrollable variant detect overflow and show the
  // prev/next scroll buttons on narrow (mobile) viewports ...
  tabContainer : {
    display       : 'flex',
    alignItems    : 'center',
    gap           : '4px',
    flexGrow      : 1,
    minWidth      : 0,
    height        : '44px',
    position      : 'relative',
    paddingTop    : '8px',
    paddingBottom : '8px'
  },
  tabRow : {
    display                        : 'flex',
    flexGrow                       : 1,
    minWidth                       : 0,
    // `fullBleed` sits flush against its container's own left edge instead
    // (see tabRoot above) ...
    paddingLeft                    : ({fullBleed}: TabViewStyleProps) => (fullBleed ? 0 : '24px'),
    [theme.breakpoints.down('sm')] : {
      paddingLeft : 0
    }
  },
  scrollButton : {
    flexShrink : 0
  },
  // `fullBleed` only - the unclassed wrapper around MUI's Tabs is a flex item
  // of tabRow and otherwise shrink-wraps its content, so any percentage
  // width on the tabs (e.g. TagsTabBar's `calc(50% - 8px)`) resolved against
  // that content-sized box and each tab collapsed to ~25% of the bar. Growing
  // it to the row's full width gives those percentages a definite basis ...
  tabWrapperFullBleed : {
    flexGrow : 1,
    minWidth : 0
  },
  // current theme only (applied via classnames when useThemeContext reports
  // the current theme - static rules rather than toggleThemeStyle thunks,
  // since JSS moves function values into a dynamic sheet where the nested
  // '&:active' rules below don't reliably apply): the button's own box
  // carries the DLS direction-specific shadow - a 12px-spread white shadow
  // thrown toward the tab row (fading out/clipping whatever tab slides
  // underneath, so no tab edge or square corner shows next to the rounded
  // button) plus a soft dark drop shadow on the opposite side. Positioned +
  // z-indexed so the left button's shadow paints over the tab row that
  // follows it in the DOM ...
  scrollButtonCurrent : {
    position     : 'relative',
    zIndex       : 1,
    borderRadius : '8px'
  },
  // IconButton's control mode resets root's box-shadow on :active - restated
  // here so the fade doesn't drop out (and expose the tabs) while pressing ...
  scrollButtonLeft : {
    boxShadow  : dlsShadow(6, 2),
    '&:active' : {
      boxShadow : dlsShadow(6, 2)
    }
  },
  scrollButtonRight : {
    boxShadow  : dlsShadow(6.2, 2.2),
    '&:active' : {
      boxShadow : dlsShadow(6.2, 2.2)
    }
  },
  // wired to MUI's Tabs `classes.scroller` slot below - this app's
  // StylesProvider is configured with `disableGlobal: true`
  // (packages/dls/src/hoc/withStyleProvider.tsx), so MUI's predictable
  // ".MuiTabs-scroller" global class name is never actually rendered to the
  // DOM; only the app-prefixed local class exists. Giving the scroller our
  // own named (empty) class lets us reliably locate it via classes.scroller
  // instead of a global selector that would never match ...
  scroller      : {},
  // MUI's own .MuiTabs-flexContainer - deliberately left unconstrained on
  // width so it can be wider than tabRoot/tabContainer when tabs overflow.
  // Inter-tab spacing lives here (rather than as horizontal padding on each
  // Tab's own root) so a Tab's root is exactly as wide as its pill
  // background (current/Tab.tsx's wrapper) - MUI measures the selected
  // Tab's root to position/size the indicator, so any extra horizontal
  // padding on root beyond wrapper's own would make the indicator wider
  // than the pill it's meant to underline ...
  flexContainer : {
    gap          : '16px',
    // fills the gaps between tabs left by each tab's own border-bottom
    // (current/Tab.tsx's materialRoot) - this element (MUI's base
    // `.MuiTabs-flexContainer`, only `{display:'flex'}` in MUI's own base
    // styles) has none of tabRoot's competing minHeight/overflow defaults,
    // so unlike tabRoot this is a safe place for a continuous line. Height
    // and box-sizing are stated explicitly (matching materialRoot exactly)
    // rather than left to this element's own natural content sizing, which
    // left a hairline gap between the two borders at the seam where a
    // tab's own border ends and this one shows through ...
    height       : '44px',
    boxSizing    : 'border-box',
    borderBottom : dlsTabIndicatorBorder('resting')
  },
  tabLayout : {
    gap     : '8px',
    display : 'flex'
  }
});

// declared at module scope (the standard MUI makeStyles pattern - this file
// has no style overrides to merge, so useExtendStyles(getTabViewStyles)
// would be functionally identical, but that wrapper is itself named with a
// `use` prefix and calling it outside a component/hook trips eslint's
// react-hooks/rules-of-hooks) so makeStyles is only invoked once - calling
// it fresh on every render regenerates every class name string
// (classes.scroller, classes.tabRoot, ...) each time, since MUI's
// generateClassName suffix comes from a global rule counter. That
// instability was cascading into getScroller/updateScrollState/
// debouncedUpdateScrollState (all useCallback'd against classes.scroller)
// and re-triggering the selected-tab scrollIntoView effect below on every
// render, not just on mount/selection - theme reactivity is unaffected,
// since the returned hook itself still re-reads the theme from context on
// every call ...
const useTabViewStyles = makeStyles((theme: DLSTheme) => getTabViewStyles(theme));

/**
 * @TabView
 *
 * Defines the Tabs component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 **
 *  <TabView tabItems={tabs} onChange={handleChange} value={value} />
 *
 *  // overriding each Tab's underlying MaterialTab styles ...
 *  <TabView tabItems={tabs}
 *    onChange={handleChange}
 *    value={value}
 *    tabOverrides={(theme) => ({materialRoot : {width : 'fit-content'}})} />
 *
 * ```
 *
 */
export const TabView = ({
  tabItems,
  onChange,
  value,
  view,
  tabOverrides,
  variant = 'fullWidth',
  reInitialize = false,
  passSelected = false,
  fullBleed = false,
  hideTabBackground = false
}: TabPropTypes) => {
  // always calls the same, module-scope-hoisted useTabViewStyles hook - see
  // its own comment for why that stability matters here specifically.
  // switching which underlying hook gets invoked from render to render
  // (e.g. a useMemo choosing between this hook and a dynamically-created
  // makeStyles hook depending on whether `overrides` is passed) breaks
  // React's own hook-order tracking at runtime, even though it reads fine
  // to eslint's static rules-of-hooks check - so `overrides` (TabView's own
  // style override prop) isn't wired up here; no current caller passes it.
  // tabOverrides (per-Tab overrides, forwarded to current/Tab.tsx below)
  // still works, since that goes through Tab's own useExtendStyles call,
  // which has no such stability requirement ...
  const classes                              = useTabViewStyles({fullBleed});
  const {isCurrent: isCurrentTheme}          = useThemeContext();
  // DLS overflow navigation buttons are 32x32 in the current theme; legacy
  // keeps BackButton/ForwardButton's own 44px button / 24px frame defaults ...
  const scrollButtonSize                     = isCurrentTheme ? 32 : 44;
  const scrollButtonFrameSize                = isCurrentTheme ? 32 : 24;
  const tabClasses                          = useMemo(() => ({
    flexContainer : classes.flexContainer,
    indicator     : classes.customTabIndicator,
    scroller      : classes?.scroller,
    root          : classes.tabRoot
  }), [classes.flexContainer, classes.customTabIndicator, classes.scroller, classes.tabRoot]);
  const [initializeTabs, setInitializeTabs] = useState(false);
  const tabRowRef                           = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft]   = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // mirrors NavigationTopBar's scroll-state tracking - the scroller isn't
  // ours to ref directly (it's rendered internally by MUI's Tabs), so it's
  // located via querySelector against our own wrapping row instead, using
  // our own classes.scroller (see its definition above for why the global
  // ".MuiTabs-scroller" selector can't be used here) ...
  const getScroller = useCallback((): HTMLElement | null => (
    (classes.scroller && tabRowRef.current?.querySelector(`.${classes.scroller}`)) || null
  ), [classes.scroller]);

  const updateScrollState = useCallback(() => {
    const element = getScroller();
    if (element) {
      setCanScrollLeft(element.scrollLeft > 0);
      setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 1);
    }
  }, [getScroller]);

  // canScrollLeft/Right mounting or unmounting the prev/next buttons mid
  // -animation changes the row's own available width, which visibly
  // disrupts an in-progress manual scroll (e.g. from clicking the button
  // itself). Debouncing the 'scroll'-driven recompute lets the current
  // scroll (manual or button-triggered) finish before the buttons are
  // allowed to react to it ...
  const scrollDebounceRef           = useRef<number | null>(null);
  const debouncedUpdateScrollState  = useCallback(() => {
    if (scrollDebounceRef.current) {
      window.clearTimeout(scrollDebounceRef.current);
    }
    scrollDebounceRef.current = window.setTimeout(updateScrollState, 120);
  }, [updateScrollState]);

  const scrollBy = useCallback((direction: -1 | 1) => {
    getScroller()?.scrollBy({
      left     : direction * 240,
      behavior : 'smooth'
    });
  }, [getScroller]);

  const onScrollLeft = useCallback(() => {
    scrollBy(-1);
  }, [scrollBy]);

  const onScrollRight = useCallback(() => {
    scrollBy(1);
  }, [scrollBy]);

  useEffect(() => {
    if (view) {
      const listener = view?.popup.watch('visible', function() {
        if (view?.popup.visible) {
          setInitializeTabs(true);
        }
        return function() {
          listener?.remove();
        };
      });
    } else {
      setInitializeTabs(true);
    }
  }, []);

  useEffect(() => {
    setInitializeTabs(true);
  }, [reInitialize]);

  useEffect(() => {
    if (initializeTabs) {
      const element = getScroller();
      if (element) {
        updateScrollState();
        // a single synchronous read here can land before the tabs' own
        // layout has settled (e.g. while a sibling toolbar is still sizing),
        // so a ResizeObserver is used instead of a one-shot check - it
        // re-fires as both the scroller and its content settle into their
        // final size ...
        const observer = new ResizeObserver(updateScrollState);
        observer.observe(element);
        if (element.firstElementChild) {
          observer.observe(element.firstElementChild);
        }
        element.addEventListener('scroll', debouncedUpdateScrollState);
        return () => {
          observer.disconnect();
          element.removeEventListener('scroll', debouncedUpdateScrollState);
          if (scrollDebounceRef.current) {
            window.clearTimeout(scrollDebounceRef.current);
          }
        };
      }
    }
  }, [initializeTabs, tabItems?.length, getScroller, updateScrollState, debouncedUpdateScrollState]);

  // keeps the selected tab in view and re-derives scroll state whenever the
  // selection changes - selecting a partially/fully offscreen tab must move
  // the scroller (and refresh canScrollLeft/canScrollRight) even though that
  // scroll wasn't triggered by the user directly scrolling ...
  useEffect(() => {
    if (initializeTabs) {
      const frame = window.requestAnimationFrame(() => {
        const selected = getScroller()?.querySelector('[aria-selected="true"]');
        selected?.scrollIntoView({
          behavior : 'smooth',
          block    : 'nearest',
          inline   : 'center'
        });
        updateScrollState();
      });
      return () => window.cancelAnimationFrame(frame);
    }
  }, [initializeTabs, value, getScroller, updateScrollState]);

  return (
    <div className={classes.tabContainer}>
      <Render.If condition={canScrollLeft}>
        <BackButton className={
          classnames(classes.scrollButton, isCurrentTheme && [classes.scrollButtonCurrent, classes.scrollButtonLeft])
        }
        aria-label='Scroll tabs left'
        controlButtonSize={scrollButtonSize}
        controlFrameSize={scrollButtonFrameSize}
        onClick={onScrollLeft} />
      </Render.If>
      <div className={classes.tabRow} ref={tabRowRef}>
        {/*
          deliberately unclassed - classes.tabRoot is passed below as MUI's
          own Tabs `classes.root` slot already, on the actual Tabs root
          element; applying it here too (an outer wrapper around that same
          element) previously double-drew tabRoot's border-bottom on two
          nested boxes, rendering as two visibly offset lines. Only
          `fullBleed` adds a (layout-only) class, so tabs can resolve
          percentage widths against the full row ...
        */}
        <div className={fullBleed ? classes.tabWrapperFullBleed : undefined}>
          <Render.If condition={initializeTabs}>
            <ScrollableTabs value={value}
              variant={variant}
              onChange={onChange}
              tabItems={tabItems}
              passSelected={passSelected}
              tabOverrides={tabOverrides}
              hideTabBackground={hideTabBackground}
              tabClasses={tabClasses} />
          </Render.If>
        </div>
      </div>
      <Render.If condition={canScrollRight}>
        <ForwardButton className={
          classnames(classes.scrollButton, isCurrentTheme && [classes.scrollButtonCurrent, classes.scrollButtonRight])
        }
        aria-label='Scroll tabs right'
        controlButtonSize={scrollButtonSize}
        controlFrameSize={scrollButtonFrameSize}
        onClick={onScrollRight} />
      </Render.If>
    </div>
  );
};

export default memo(TabView);
